import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function ProjectSettings() {
  const { projectKey } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [fields, setFields] = useState({ testCaseFields: {}, customFields: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'standard');

  // Members tab state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ userId: '', teamId: '', role: 'tester', type: 'user' });
  const [addMemberError, setAddMemberError] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [projectRoles, setProjectRoles] = useState([]);

  // Issue Tracker config tab state
  const [trackerForm, setTrackerForm] = useState({
    type: 'none',
    enabled: false,
    trackerName: '',
    idLabel: '',
    urlLabel: '',
    statusLabel: '',
    projectKey: '',  // JIRA-specific
  });
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerLoaded, setTrackerLoaded] = useState(false);
  const [trackerSaved, setTrackerSaved] = useState(false);
  const [trackerError, setTrackerError] = useState('');
  // Legacy JIRA project key (used when type=jira)
  const [jiraForm, setJiraForm] = useState({ projectKey: '' });
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraConfigLoaded, setJiraConfigLoaded] = useState(false);
  const [jiraSaved, setJiraSaved] = useState(false);
  const [jiraError, setJiraError] = useState('');
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);

  // JIRA Import tab state
  const [jiraImportProjects, setJiraImportProjects] = useState([]);
  const [jiraImportConnected, setJiraImportConnected] = useState(false);
  const [jiraImportLoading, setJiraImportLoading] = useState(false);
  // Import panel
  const [importJiraKey, setImportJiraKey] = useState('');
  const [importIssueType, setImportIssueType] = useState('');
  const [importIssueTypes, setImportIssueTypes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  // Search panel
  const [searchMode, setSearchMode] = useState('simple');
  const [searchJiraKey, setSearchJiraKey] = useState('');
  const [searchIssueType, setSearchIssueType] = useState('');
  const [searchIssueTypes, setSearchIssueTypes] = useState([]);
  const [jqlQuery, setJqlQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [customFieldForm, setCustomFieldForm] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: '',
  });

  useEffect(() => {
    if (projectKey) {
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectId) {
      fetchFields();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId && activeTab === 'members') {
      fetchMembers();
      if (projectRoles.length === 0) {
        api.get('/roles?scope=project').then(r => setProjectRoles(r.data.data || [])).catch(() => {});
      }
    }
    if (projectId && activeTab === 'jira-import' && !jiraImportConnected && !jiraImportLoading) {
      loadJiraImportProjects();
    }
  }, [projectId, activeTab]);

  // Update import issue types when import project changes
  useEffect(() => {
    const p = jiraImportProjects.find(j => j.key === importJiraKey);
    setImportIssueTypes(p?.issueTypes || []);
    setImportIssueType('');
  }, [importJiraKey, jiraImportProjects]);

  // Update search issue types when search project changes
  useEffect(() => {
    const p = jiraImportProjects.find(j => j.key === searchJiraKey);
    setSearchIssueTypes(p?.issueTypes || []);
    setSearchIssueType('');
  }, [searchJiraKey, jiraImportProjects]);

  useEffect(() => {
    if (projectId && !jiraConfigLoaded) {
      loadJiraConfig();
    }
    if (projectId && !trackerLoaded) {
      loadTrackerConfig();
    }
  }, [projectId]);

  const loadJiraImportProjects = async () => {
    setJiraImportLoading(true);
    try {
      const res = await api.get('/jira/projects');
      setJiraImportProjects(res.data.data || []);
      setJiraImportConnected(true);
    } catch {
      setJiraImportConnected(false);
    } finally {
      setJiraImportLoading(false);
    }
  };

  const runJiraImport = async () => {
    if (!importJiraKey) { setImportError('Select a JIRA project to import from.'); return; }
    setImporting(true);
    setImportResult(null);
    setImportError('');
    try {
      const res = await api.post('/jira/requirements/import-issues', {
        projectId,
        jiraProjectKey: importJiraKey,
        issueType: importIssueType || undefined,
      });
      setImportResult(res.data);
    } catch (err) {
      setImportError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  };

  const runJiraSearch = async () => {
    let jql = '';
    if (searchMode === 'jql') {
      if (!jqlQuery.trim()) { setSearchError('Enter a JQL query.'); return; }
      jql = jqlQuery.trim();
    } else {
      if (!searchJiraKey) { setSearchError('Select a JIRA project to search.'); return; }
      jql = `project = "${searchJiraKey}"`;
      if (searchIssueType) jql += ` AND issuetype = "${searchIssueType}"`;
      jql += ' ORDER BY updated DESC';
    }
    setSearching(true);
    setSearchError('');
    setSearchResults([]);
    try {
      const res = await api.get('/jira/issues', { params: { jql, maxResults: 50 } });
      setSearchResults(res.data.data || []);
      if ((res.data.data || []).length === 0) setSearchError('No issues matched.');
    } catch (err) {
      setSearchError(err.response?.data?.error || err.message);
    } finally {
      setSearching(false);
    }
  };

  const getJiraPriorityIcon = (priority) => {
    if (!priority) return '⬜';
    const p = priority.toLowerCase();
    if (p === 'blocker' || p === 'critical') return '🔴';
    if (p === 'high' || p === 'major') return '🟠';
    if (p === 'medium' || p === 'normal') return '🟡';
    return '🟢';
  };

  const getJiraStatusBadge = (status) => {
    if (!status) return <span className="badge bg-secondary">—</span>;
    const s = status.toLowerCase();
    if (s.includes('done') || s.includes('closed') || s.includes('resolved'))
      return <span className="badge bg-success">{status}</span>;
    if (s.includes('progress') || s.includes('review'))
      return <span className="badge bg-primary">{status}</span>;
    if (s.includes('blocked')) return <span className="badge bg-danger">{status}</span>;
    return <span className="badge bg-secondary">{status}</span>;
  };

  const fetchMembers = async () => {
    if (!projectId) return;
    setMembersLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/members`);
      setMembers(res.data.data || []);
    } catch (err) {
      console.error('Failed to load members:', err.message);
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchUsersAndTeams = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        api.get('/auth/users').catch(() => ({ data: { data: [] } })),
        api.get('/teams'),
      ]);
      setAllUsers(usersRes.data.data || usersRes.data.users || []);
      setAllTeams(teamsRes.data.data || []);
    } catch (err) {
      console.error('Failed to load users/teams:', err.message);
    }
  };

  const openAddMemberModal = () => {
    fetchUsersAndTeams();
    setAddMemberForm({ userId: '', teamId: '', role: 'tester', type: 'user' });
    setAddMemberError('');
    setShowAddMemberModal(true);
  };

  const addMember = async () => {
    setAddMemberLoading(true);
    setAddMemberError('');
    try {
      const payload = { role: addMemberForm.role };
      if (addMemberForm.type === 'user') payload.userId = addMemberForm.userId;
      else payload.teamId = addMemberForm.teamId;
      await api.post(`/projects/${projectId}/members`, payload);
      setShowAddMemberModal(false);
      fetchMembers();
    } catch (err) {
      setAddMemberError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAddMemberLoading(false);
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await api.delete(`/projects/${projectId}/members/${memberId}`);
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await api.put(`/projects/${projectId}/members/${memberId}`, { role: newRole });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change role');
    }
  };

  const generateApiKey = async (memberId) => {
    try {
      const res = await api.post(`/projects/${projectId}/members/${memberId}/apikey`);
      setGeneratedApiKey(res.data.apiKey);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate API key');
    }
  };

  const revokeApiKey = async (memberId) => {
    if (!window.confirm('Revoke this API key? The CI pipeline will lose access immediately.')) return;
    try {
      await api.delete(`/projects/${projectId}/members/${memberId}/apikey`);
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke API key');
    }
  };

  const loadJiraConfig = async () => {
    try {
      const res = await api.get(`/jira/project-config/${projectId}`);
      const cfg = res.data;
      setJiraForm({ projectKey: cfg.projectKey || '' });
      setJiraConfigLoaded(true);
    } catch (err) {
      console.error('Failed to load JIRA config:', err.message);
    }
  };

  const loadTrackerConfig = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/issue-tracker`);
      const cfg = res.data.data || {};
      setTrackerForm(prev => ({
        ...prev,
        type: cfg.type || 'none',
        enabled: cfg.enabled || false,
        trackerName: cfg.trackerName || '',
        idLabel: cfg.idLabel || '',
        urlLabel: cfg.urlLabel || '',
        statusLabel: cfg.statusLabel || '',
      }));
      setTrackerLoaded(true);
    } catch (err) {
      console.error('Failed to load issue tracker config:', err.message);
    }
  };

  const saveTrackerConfig = async () => {
    setTrackerLoading(true);
    setTrackerError('');
    setTrackerSaved(false);
    try {
      await api.put(`/projects/${projectId}/issue-tracker`, trackerForm);
      // Also save JIRA project key if type=jira
      if (trackerForm.type === 'jira') {
        await api.put(`/jira/project-config/${projectId}`, jiraForm);
      }
      setTrackerSaved(true);
      setTimeout(() => setTrackerSaved(false), 3000);
    } catch (err) {
      setTrackerError(err.response?.data?.error || err.message);
    } finally {
      setTrackerLoading(false);
    }
  };

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchFields = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectId}/fields`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFields(data.data);
      }
    } catch (err) {
      console.error('Error loading fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStandardField = async (fieldName, enabled, values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectId}/fields/standard`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fieldName, enabled, values }),
      });

      if (response.ok) {
        fetchFields();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update field');
      }
    } catch (err) {
      alert('Error updating field');
      console.error(err);
    }
  };

  const handleCustomFieldSubmit = async e => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...customFieldForm,
        options: customFieldForm.options.split(',').map(o => o.trim()).filter(Boolean),
      };

      const url = editingField
        ? `${API_URL}/projects/${projectId}/fields/custom/${editingField.name}`
        : `${API_URL}/projects/${projectId}/fields/custom`;
      const method = editingField ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCustomFieldModal(false);
        setEditingField(null);
        setCustomFieldForm({ name: '', label: '', type: 'text', required: false, options: '' });
        fetchFields();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to save custom field');
      }
    } catch (err) {
      alert('Error saving custom field');
      console.error(err);
    }
  };

  const deleteCustomField = async fieldName => {
    if (!window.confirm('Are you sure you want to delete this custom field?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/projects/${projectId}/fields/custom/${fieldName}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        fetchFields();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete field');
      }
    } catch (err) {
      alert('Error deleting field');
      console.error(err);
    }
  };

  const editCustomField = field => {
    setEditingField(field);
    setCustomFieldForm({
      name: field.name,
      label: field.label,
      type: field.type,
      required: field.required,
      options: Array.isArray(field.options) ? field.options.join(', ') : '',
    });
    setShowCustomFieldModal(true);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Project Settings</h1>
          </div>
        </div>
        <div className="zq-spinner-wrap">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  const standardFields = [
    { name: 'priority', label: 'Priority' },
    { name: 'status', label: 'Status' },
    { name: 'category', label: 'Test Category' },
    { name: 'severity', label: 'Severity' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/projects">Projects</Link></li>
              <li className="breadcrumb-item">
                <Link to={`/projects/${projectKey}`}>{project?.name}</Link>
              </li>
              <li className="breadcrumb-item active">Settings</li>
            </ol>
          </nav>
          <h1 className="page-title">Project Settings</h1>
          <p className="page-subtitle">Manage access, roles, fields and integrations</p>
        </div>
      </div>

      <div className="container-fluid">
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'members' ? 'active' : ''}`}
              onClick={() => setActiveTab('members')}
            >
              👥 Members &amp; Roles
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'standard' ? 'active' : ''}`}
              onClick={() => setActiveTab('standard')}
            >
              Standard Fields
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'custom' ? 'active' : ''}`}
              onClick={() => setActiveTab('custom')}
            >
              Custom Fields
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'tracker' ? 'active' : ''}`}
              onClick={() => setActiveTab('tracker')}
            >
              🔗 Issue Tracker
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'jira-import' ? 'active' : ''}`}
              onClick={() => setActiveTab('jira-import')}
            >
              ⬇️ JIRA Import
            </button>
          </li>
        </ul>

        {activeTab === 'standard' && (
          <div className="row">
            {standardFields.map(field => {
              const fieldData = fields.testCaseFields[field.name] || { enabled: false, values: [] };
              return (
                <div key={field.name} className="col-md-6 mb-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">{field.label}</h5>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={fieldData.enabled}
                            onChange={e => updateStandardField(field.name, e.target.checked, fieldData.values)}
                          />
                          <label className="form-check-label">Enabled</label>
                        </div>
                      </div>
                      {fieldData.enabled && (
                        <div>
                          <label className="form-label">Values (one per line)</label>
                          <textarea
                            className="form-control"
                            rows="4"
                            value={fieldData.values.join('\n')}
                            onChange={e =>
                              updateStandardField(
                                field.name,
                                fieldData.enabled,
                                e.target.value.split('\n').filter(v => v.trim())
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'tracker' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 20 }}>
              Choose an external issue tracker to link with your requirements and test cases.
              Fields and buttons only appear when a tracker is enabled.
            </p>

            {trackerError && (
              <div className="alert alert-danger alert-dismissible">
                {trackerError}
                <button className="btn-close" onClick={() => setTrackerError('')}></button>
              </div>
            )}
            {trackerSaved && <div className="alert alert-success">✅ Saved.</div>}

            {/* Tracker type selector */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Issue Tracker Type</label>
                <select
                  className="form-select"
                  value={trackerForm.type}
                  onChange={e => setTrackerForm(prev => ({
                    ...prev,
                    type: e.target.value,
                    enabled: e.target.value !== 'none',
                  }))}
                >
                  <option value="none">None (disable tracker integration)</option>
                  <option value="jira">JIRA</option>
                  <option value="other">Other (GitHub, Linear, Azure DevOps, etc.)</option>
                </select>
              </div>

              {trackerForm.type !== 'none' && (
                <div className="col-md-2 d-flex align-items-end">
                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="trackerEnabled"
                      checked={trackerForm.enabled}
                      onChange={e => setTrackerForm(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="trackerEnabled">Enabled</label>
                  </div>
                </div>
              )}
            </div>

            {/* JIRA-specific config */}
            {trackerForm.type === 'jira' && trackerForm.enabled && (
              <div className="card mb-4">
                <div className="card-header"><strong>JIRA Configuration</strong></div>
                <div className="card-body row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Default JIRA Project Key</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. PROJ"
                      value={jiraForm.projectKey}
                      onChange={e => setJiraForm(prev => ({ ...prev, projectKey: e.target.value.toUpperCase() }))}
                    />
                    <div className="form-text">Issues will default to this JIRA project key.</div>
                  </div>
                  <div className="col-12">
                    <Link to={`/jira?projectId=${projectId}`} className="btn btn-outline-secondary btn-sm">
                      ↗ Open JIRA Credentials Settings
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Generic tracker config */}
            {trackerForm.type === 'other' && trackerForm.enabled && (
              <div className="card mb-4">
                <div className="card-header"><strong>Custom Labels</strong></div>
                <div className="card-body row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Tracker Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. GitHub, Linear, Azure DevOps"
                      value={trackerForm.trackerName}
                      onChange={e => setTrackerForm(prev => ({ ...prev, trackerName: e.target.value }))}
                    />
                    <div className="form-text">Used in button labels like "Link GitHub Issue".</div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">ID Field Label</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. GitHub Issue #, Linear ID"
                      value={trackerForm.idLabel}
                      onChange={e => setTrackerForm(prev => ({ ...prev, idLabel: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">URL Field Label</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Issue URL"
                      value={trackerForm.urlLabel}
                      onChange={e => setTrackerForm(prev => ({ ...prev, urlLabel: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Status Field Label</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Issue Status"
                      value={trackerForm.statusLabel}
                      onChange={e => setTrackerForm(prev => ({ ...prev, statusLabel: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {trackerForm.type !== 'none' && (
              <button
                className="btn btn-primary"
                onClick={saveTrackerConfig}
                disabled={trackerLoading}
              >
                {trackerLoading
                  ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Saving…</>
                  : '💾 Save'}
              </button>
            )}

            <div className="card mt-4" style={{ background: 'var(--zq-info-light)', borderColor: 'var(--zq-info)' }}>
              <div className="card-body" style={{ padding: '14px 18px' }}>
                <p className="mb-2" style={{ fontWeight: 600, fontSize: 13, color: '#075985' }}>What issue tracker integration enables</p>
                <ul className="mb-0" style={{ fontSize: 13, color: '#075985', paddingLeft: 18 }}>
                  <li>Link Requirements and Test Cases to external issues</li>
                  <li>Tracker ID, URL and Status fields appear in forms and tables</li>
                  <li>Import template includes tracker columns</li>
                  {trackerForm.type === 'jira' && <>
                    <li>Full JIRA sync — create issues, sync status, file bugs from test runs</li>
                    <li>Search JIRA issues directly from the link dialog</li>
                  </>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-1">Project Members</h5>
                <p className="text-muted small mb-0">Control who can access this project and what they can do.</p>
              </div>
              <button className="btn btn-primary btn-sm" onClick={openAddMemberModal}>
                + Add Member
              </button>
            </div>

            {membersLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
            ) : members.length === 0 ? (
              <div className="alert alert-info">No members yet. Add users or teams to grant project access.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Member</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>API Key</th>
                      <th>Added</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const name = m.userId
                        ? (m.userId.username || m.userId.email || String(m.userId))
                        : (m.teamId?.name || String(m.teamId));
                      const isTeam = !m.userId;
                      return (
                        <tr key={m._id}>
                          <td>
                            <span className="fw-semibold">{name}</span>
                            {m.userId?.email && !isTeam && (
                              <div className="text-muted small">{m.userId.email}</div>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${isTeam ? 'bg-info' : 'bg-secondary'} bg-opacity-15 text-dark`}>
                              {isTeam ? '👥 Team' : '👤 User'}
                            </span>
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={m.role}
                              style={{ minWidth: 140 }}
                              onChange={e => changeRole(m._id, e.target.value)}
                            >
                              {(projectRoles.length ? projectRoles : [
                                { role: 'project_admin', label: 'Project Admin' },
                                { role: 'lead', label: 'Lead' },
                                { role: 'tester', label: 'Tester' },
                                { role: 'reviewer', label: 'Reviewer' },
                                { role: 'ci_runner', label: 'CI Runner' },
                              ]).map(r => (
                                <option key={r.role} value={r.role}>{r.label || r.role.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {m.role === 'ci_runner' ? (
                              <div className="d-flex align-items-center gap-2">
                                {m.apiKeyPrefix ? (
                                  <>
                                    <code className="small">{m.apiKeyPrefix}…</code>
                                    <button className="btn btn-outline-warning btn-sm" onClick={() => generateApiKey(m._id)}>Rotate</button>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => revokeApiKey(m._id)}>Revoke</button>
                                  </>
                                ) : (
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => generateApiKey(m._id)}>Generate Key</button>
                                )}
                              </div>
                            ) : <span className="text-muted small">N/A</span>}
                          </td>
                          <td className="text-muted small">{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '—'}</td>
                          <td>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => removeMember(m._id)}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Role legend */}
            {projectRoles.length > 0 && (
              <div className="card mt-4">
                <div className="card-header d-flex align-items-center justify-content-between fw-semibold">
                  <span>Role Permissions Reference</span>
                  <a href="/settings/roles" className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12 }}>Manage Roles ↗</a>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 130 }}>Role</th>
                          <th className="text-center">Create</th>
                          <th className="text-center">Edit</th>
                          <th className="text-center">Delete</th>
                          <th className="text-center">Push Results</th>
                          <th className="text-center">Manage Members</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectRoles.map(r => {
                          const p = r.permissions || [];
                          const has = key => p.some(x => x.includes(key));
                          return (
                            <tr key={r.role}>
                              <td>
                                <code style={{ fontSize: 11, color: '#64748b' }}>{r.role}</code>
                                {r.isCustom && <span className="ms-1" style={{ fontSize: 10, color: '#059669', background: '#d1fae5', borderRadius: 3, padding: '1px 4px' }}>Custom</span>}
                              </td>
                              <td className="text-center">{has(':create') ? '✅' : '—'}</td>
                              <td className="text-center">{has(':update') ? '✅' : '—'}</td>
                              <td className="text-center">{has(':delete') ? '✅' : '—'}</td>
                              <td className="text-center">{p.includes('testrun:push_results') ? '✅' : '—'}</td>
                              <td className="text-center">{p.includes('member:manage') ? '✅' : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Member Modal */}
        {showAddMemberModal && (
          <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Member to Project</h5>
                  <button className="btn-close" onClick={() => setShowAddMemberModal(false)} />
                </div>
                <div className="modal-body">
                  {addMemberError && <div className="alert alert-danger py-2">{addMemberError}</div>}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Add a</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" id="typeUser" checked={addMemberForm.type === 'user'} onChange={() => setAddMemberForm(p => ({...p, type:'user', teamId:''}))} />
                        <label className="form-check-label" htmlFor="typeUser">👤 User</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" id="typeTeam" checked={addMemberForm.type === 'team'} onChange={() => setAddMemberForm(p => ({...p, type:'team', userId:''}))} />
                        <label className="form-check-label" htmlFor="typeTeam">👥 Team</label>
                      </div>
                    </div>
                  </div>
                  {addMemberForm.type === 'user' ? (
                    <div className="mb-3">
                      <label className="form-label">User</label>
                      <select className="form-select" value={addMemberForm.userId} onChange={e => setAddMemberForm(p => ({...p, userId: e.target.value}))}>
                        <option value="">Select a user…</option>
                        {allUsers.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.username} ({u.email})</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <label className="form-label">Team</label>
                      <select className="form-select" value={addMemberForm.teamId} onChange={e => setAddMemberForm(p => ({...p, teamId: e.target.value}))}>
                        <option value="">Select a team…</option>
                        {allTeams.map(t => <option key={t._id || t.id} value={t._id || t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={addMemberForm.role} onChange={e => setAddMemberForm(p => ({...p, role: e.target.value}))}>
                      {(projectRoles.length ? projectRoles : [
                        { role: 'project_admin', label: 'Project Admin', description: 'Full control' },
                        { role: 'lead', label: 'Lead', description: 'Create/edit/delete all resources' },
                        { role: 'tester', label: 'Tester', description: 'Create & edit, no delete' },
                        { role: 'reviewer', label: 'Reviewer', description: 'Read only' },
                        { role: 'ci_runner', label: 'CI Runner', description: 'Push results via API key' },
                      ]).map(r => (
                        <option key={r.role} value={r.role}>
                          {r.label || r.role.replace(/_/g, ' ')}{r.description ? ` — ${r.description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowAddMemberModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={addMember} disabled={addMemberLoading || (!addMemberForm.userId && !addMemberForm.teamId)}>
                    {addMemberLoading ? 'Adding…' : 'Add Member'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generated API Key Modal */}
        {generatedApiKey && (
          <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">🔑 API Key Generated</h5>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning">
                    <strong>Copy this key now.</strong> It will not be shown again.
                  </div>
                  <div className="input-group">
                    <input type="text" className="form-control font-monospace" readOnly value={generatedApiKey} />
                    <button className="btn btn-outline-secondary" onClick={() => navigator.clipboard?.writeText(generatedApiKey)}>Copy</button>
                  </div>
                  <p className="text-muted small mt-2">Use this as the <code>X-Api-Key</code> header in your CI/CD pipeline.</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => { setGeneratedApiKey(null); fetchMembers(); }}>Done</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'custom' && (
          <div>
            <div className="section-header mb-4">
              <h5 className="section-title">Custom Fields</h5>
              <button className="btn btn-primary btn-sm" onClick={() => setShowCustomFieldModal(true)}>
                + Add Custom Field
              </button>
            </div>

            <table className="table table-hover bg-white">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Label</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Options</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fields.customFields.map(field => (
                  <tr key={field.name}>
                    <td>
                      <code>{field.name}</code>
                    </td>
                    <td>{field.label}</td>
                    <td>
                      <span className="badge bg-info">{field.type}</span>
                    </td>
                    <td>{field.required ? '✓' : '-'}</td>
                    <td>
                      {field.options && field.options.length > 0
                        ? field.options.join(', ')
                        : '-'}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" onClick={() => editCustomField(field)}>
                          Edit
                        </button>
                        <button className="btn btn-outline-danger" onClick={() => deleteCustomField(field.name)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {fields.customFields.length === 0 && (
              <div className="alert alert-info">
                No custom fields defined. Add custom fields to extend test case data.
              </div>
            )}
          </div>
        )}

        {activeTab === 'jira-import' && (
          <div>
            {jiraImportLoading && (
              <div className="zq-spinner-wrap">
                <span className="spinner-border" style={{ color: 'var(--zq-primary)' }} />
                <span style={{ fontSize: 13, color: 'var(--zq-text-muted)' }}>Loading JIRA projects…</span>
              </div>
            )}

            {!jiraImportLoading && !jiraImportConnected && (
              <div className="empty-state">
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
                <h5>JIRA not connected</h5>
                <p className="text-muted">Configure your JIRA credentials in Global Settings first.</p>
                <Link to="/jira" className="btn btn-primary">Go to JIRA Settings</Link>
              </div>
            )}

            {!jiraImportLoading && jiraImportConnected && (
              <>
                {/* ── Import section ── */}
                <div className="card mb-4">
                  <div className="card-header">
                    <strong>Import Issues as Requirements</strong>
                  </div>
                  <div className="card-body">
                    <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 16 }}>
                      Pull issues from a JIRA project into <strong>{project?.name}</strong> as requirements.
                      Already-imported issues are skipped automatically.
                    </p>

                    {importError && (
                      <div className="alert alert-danger alert-dismissible">
                        {importError}
                        <button className="btn-close" onClick={() => setImportError('')} />
                      </div>
                    )}
                    {importResult && (
                      <div className="alert alert-success">
                        ✅ Imported <strong>{importResult.imported}</strong> requirement(s).
                        {importResult.skipped > 0 && (
                          <span style={{ color: 'var(--zq-text-muted)' }}> ({importResult.skipped} already existed, skipped)</span>
                        )}
                      </div>
                    )}

                    <div className="row g-3 align-items-end mb-3">
                      <div className="col-md-4">
                        <label className="form-label">JIRA Project *</label>
                        {jiraImportProjects.length > 0 ? (
                          <select
                            className="form-select"
                            value={importJiraKey}
                            onChange={e => setImportJiraKey(e.target.value)}
                          >
                            <option value="">— Select project —</option>
                            {jiraImportProjects.map(p => (
                              <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. PROJ"
                            value={importJiraKey}
                            onChange={e => setImportJiraKey(e.target.value.toUpperCase())}
                          />
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Issue Type <small className="text-muted">(optional — imports all if blank)</small>
                        </label>
                        {importIssueTypes.length > 0 ? (
                          <select
                            className="form-select"
                            value={importIssueType}
                            onChange={e => setImportIssueType(e.target.value)}
                          >
                            <option value="">— All types —</option>
                            {importIssueTypes.map(t => (
                              <option key={t.id} value={t.name}>{t.name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Epic, Story, Bug"
                            value={importIssueType}
                            onChange={e => setImportIssueType(e.target.value)}
                            disabled={!importJiraKey}
                          />
                        )}
                      </div>
                      <div className="col-md-4">
                        <button
                          className="btn btn-success w-100"
                          onClick={runJiraImport}
                          disabled={importing || !importJiraKey}
                        >
                          {importing ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Importing…</>
                          ) : (
                            '⬇️ Import Issues'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Search section ── */}
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Search JIRA Issues</strong>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn ${searchMode === 'simple' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setSearchMode('simple')}
                      >Simple</button>
                      <button
                        type="button"
                        className={`btn ${searchMode === 'jql' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setSearchMode('jql')}
                      >JQL</button>
                    </div>
                  </div>
                  <div className="card-body">
                    {searchMode === 'simple' ? (
                      <div className="row g-3 mb-3">
                        <div className="col-md-5">
                          <label className="form-label">JIRA Project</label>
                          {jiraImportProjects.length > 0 ? (
                            <select
                              className="form-select"
                              value={searchJiraKey}
                              onChange={e => setSearchJiraKey(e.target.value)}
                            >
                              <option value="">— All projects —</option>
                              {jiraImportProjects.map(p => (
                                <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g. PROJ"
                              value={searchJiraKey}
                              onChange={e => setSearchJiraKey(e.target.value.toUpperCase())}
                            />
                          )}
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Issue Type</label>
                          {searchIssueTypes.length > 0 ? (
                            <select
                              className="form-select"
                              value={searchIssueType}
                              onChange={e => setSearchIssueType(e.target.value)}
                            >
                              <option value="">— All types —</option>
                              {searchIssueTypes.map(t => (
                                <option key={t.id} value={t.name}>{t.name}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g. Epic, Story"
                              value={searchIssueType}
                              onChange={e => setSearchIssueType(e.target.value)}
                              disabled={!searchJiraKey}
                            />
                          )}
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <button
                            className="btn btn-outline-primary w-100"
                            onClick={runJiraSearch}
                            disabled={searching}
                          >
                            {searching ? <span className="spinner-border spinner-border-sm" /> : '🔍 Search'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder='e.g. project = "PROJ" AND issuetype = Epic ORDER BY updated DESC'
                          value={jqlQuery}
                          onChange={e => setJqlQuery(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && runJiraSearch()}
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={runJiraSearch}
                          disabled={searching}
                        >
                          {searching ? <span className="spinner-border spinner-border-sm" /> : 'Search'}
                        </button>
                      </div>
                    )}

                    {searchError && <div className="alert alert-warning py-2">{searchError}</div>}

                    {searchResults.length > 0 && (
                      <>
                        <div style={{ fontSize: 12, color: 'var(--zq-text-muted)', marginBottom: 8 }}>
                          Showing {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                        </div>
                        <div className="table-responsive">
                          <table className="table table-hover" style={{ fontSize: 13 }}>
                            <thead>
                              <tr>
                                <th>Key</th>
                                <th>Summary</th>
                                <th>Type</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th>Assignee</th>
                              </tr>
                            </thead>
                            <tbody>
                              {searchResults.map(issue => (
                                <tr key={issue.issueKey}>
                                  <td>
                                    <a
                                      href={issue.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ fontWeight: 600 }}
                                    >
                                      {issue.issueKey}
                                    </a>
                                  </td>
                                  <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {issue.summary}
                                  </td>
                                  <td>
                                    <span className="badge" style={{ background: 'var(--zq-border-light)', color: 'var(--zq-text-secondary)' }}>
                                      {issue.issueType}
                                    </span>
                                  </td>
                                  <td>{getJiraPriorityIcon(issue.priority)} {issue.priority}</td>
                                  <td>{getJiraStatusBadge(issue.status)}</td>
                                  <td><small>{issue.assignee || '—'}</small></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Custom Field Modal */}
      {showCustomFieldModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingField ? 'Edit' : 'Add'} Custom Field</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCustomFieldModal(false);
                    setEditingField(null);
                  }}
                ></button>
              </div>
              <form onSubmit={handleCustomFieldSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Field Name * (internal identifier)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={customFieldForm.name}
                      onChange={e => setCustomFieldForm({ ...customFieldForm, name: e.target.value })}
                      required
                      disabled={editingField}
                      pattern="[a-zA-Z_][a-zA-Z0-9_]*"
                      placeholder="e.g., testEnvironment"
                    />
                    <small className="text-muted">Only letters, numbers, and underscores. No spaces.</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Display Label *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={customFieldForm.label}
                      onChange={e => setCustomFieldForm({ ...customFieldForm, label: e.target.value })}
                      required
                      placeholder="e.g., Test Environment"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Field Type *</label>
                    <select
                      className="form-select"
                      value={customFieldForm.type}
                      onChange={e => setCustomFieldForm({ ...customFieldForm, type: e.target.value })}
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Text Area</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="multiselect">Multi-Select</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                    </select>
                  </div>
                  {(customFieldForm.type === 'dropdown' || customFieldForm.type === 'multiselect') && (
                    <div className="mb-3">
                      <label className="form-label">Options (comma-separated) *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={customFieldForm.options}
                        onChange={e => setCustomFieldForm({ ...customFieldForm, options: e.target.value })}
                        placeholder="Option 1, Option 2, Option 3"
                        required
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={customFieldForm.required}
                        onChange={e => setCustomFieldForm({ ...customFieldForm, required: e.target.checked })}
                      />
                      <label className="form-check-label">Required Field</label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCustomFieldModal(false);
                      setEditingField(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingField ? 'Update' : 'Add'} Field
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectSettings;
